import React, { useState, useMemo } from 'react';
import { Button, Dialog, useDialogStore } from '@ariakit/react';
import { Table } from '@tanstack/react-table';
import {
  Users,
  X,
  ChevronUp,
  ChevronDown,
  Trash2,
  Plus,
  GripVertical
} from 'lucide-react';
import { DataTableColumn } from '../types';

interface GroupingControlsProps<TData> {
  table: Table<TData>;
  groupingBarButtons?: { accessor_keys: string[]; id: string; label: string; }[]
}

export default function GroupingControls<TData>({
  table,
  groupingBarButtons,
}: GroupingControlsProps<TData>) {
  const dialog = useDialogStore();
  const [searchQuery, setSearchQuery] = useState('');
  const grouping = table.getState().grouping;
  const allColumns = table.getAllColumns();

  const groupableColumns = allColumns.filter(column => {
    const columnDef = column.columnDef as DataTableColumn<TData>;
    return columnDef.enableGrouping !== false && column.id !== 'actions';
  });

  const availableColumns = useMemo(() => {
    return groupableColumns.filter(col => !grouping.includes(col.id));
  }, [groupableColumns, grouping]);

  const filteredAvailableColumns = useMemo(() => {
    if (!searchQuery) return availableColumns;

    return availableColumns.filter(column => {
      const header = column.columnDef.header;
      const columnName = typeof header === 'string' ? header : column.id;
      return columnName.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [availableColumns, searchQuery]);

  const handleAddGroup = (columnId: string) => {
    if (!grouping.includes(columnId)) {
      table.setGrouping([...grouping, columnId]);
    }
  };

  const handleGroupBy = (columnIds: string[]) => {
    table.setGrouping(columnIds);
  };

  const handleRemoveGroup = (columnId: string) => {
    table.setGrouping(grouping.filter(id => id !== columnId));
  };

  const handleMoveGroupUp = (index: number) => {
    if (index === 0) return;
    const newGrouping = [...grouping];
    [newGrouping[index - 1], newGrouping[index]] = [newGrouping[index], newGrouping[index - 1]];
    table.setGrouping(newGrouping);
  };

  const handleMoveGroupDown = (index: number) => {
    if (index === grouping.length - 1) return;
    const newGrouping = [...grouping];
    [newGrouping[index], newGrouping[index + 1]] = [newGrouping[index + 1], newGrouping[index]];
    table.setGrouping(newGrouping);
  };

  const handleClearGroups = () => {
    table.setGrouping([]);
  };

  return (
    <>
      <Button
        onClick={dialog.show}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        data-testid="grouping-button"
      >
        <Users className="w-4 h-4" />
        Group By ({grouping.length})
      </Button>

      {groupingBarButtons?.map((wrapper, index) => (
        <Button
          key={`grouping_bar_button_${wrapper.id}`}
          onClick={() => handleGroupBy(wrapper.accessor_keys)}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          data-testid="grouping-button"
        >
          <Users className="w-4 h-4" />
          {wrapper.label || ""}
        </Button>
      ))}

      <Dialog
        store={dialog}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        backdrop={
          <div className="fixed inset-0 bg-black/50" />
        }
        data-testid="grouping-dialog"
      >
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Configure Grouping
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Add columns to group by and arrange their priority
            </p>
          </div>

          <div className="flex-1 overflow-hidden flex">
            <div className="flex-1 p-6 overflow-y-auto border-r border-gray-200">
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Active Groups ({grouping.length})
                </h3>

                {grouping.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    No grouping applied. Add columns from the right panel.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {grouping.map((columnId, index) => {
                      if (!table.getAllColumns().length) { return null; }

                      const column = table.getColumn(columnId);
                      if (!column) return null;

                      const columnName = typeof column.columnDef.header === 'string'
                        ? column.columnDef.header
                        : columnId;

                      return (
                        <div
                          key={columnId}
                          className="flex items-center gap-3 p-3 rounded-lg border bg-white border-gray-200 hover:border-gray-300"
                          data-testid={`group-item-${columnId}`}
                        >
                          <div className="cursor-move">
                            <GripVertical className="w-4 h-4 text-gray-400" />
                          </div>

                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-semibold">
                            {index + 1}
                          </div>

                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-700">
                              {columnName}
                            </span>
                          </div>

                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleMoveGroupUp(index)}
                              disabled={index === 0}
                              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Move up"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleMoveGroupDown(index)}
                              disabled={index === grouping.length - 1}
                              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Move down"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRemoveGroup(columnId)}
                              className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                              title="Remove"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="w-72 p-6 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Available Columns
              </h3>

              <input
                type="text"
                placeholder="Search columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
              />

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredAvailableColumns.length === 0 ? (
                  <div className="text-center py-4 text-gray-400 text-sm">
                    {searchQuery ? 'No columns found' : 'All columns are being grouped'}
                  </div>
                ) : (
                  filteredAvailableColumns.map(column => {
                    const columnName = typeof column.columnDef.header === 'string'
                      ? column.columnDef.header
                      : column.id;

                    return (
                      <button
                        key={column.id}
                        onClick={() => handleAddGroup(column.id)}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm bg-white border border-gray-200 rounded-md hover:bg-gray-50 hover:border-gray-300"
                        data-testid={`add-group-${column.id}`}
                      >
                        <span className="text-gray-700">{columnName}</span>
                        <Plus className="w-4 h-4 text-gray-400" />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
            <Button
              onClick={handleClearGroups}
              className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              disabled={grouping.length === 0}
              data-testid="grouping-clear-all"
            >
              <Trash2 className="w-4 h-4 mr-2 inline" />
              Clear All
            </Button>

            <div className="flex gap-3">
              <Button
                onClick={dialog.hide}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
